import React, { useState, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { formatDistanceToNow, startOfDay, endOfDay, startOfWeek, endOfWeek, subWeeks, startOfMonth, endOfMonth, subMonths, startOfYear, subDays } from 'date-fns';
import { AlertCircle, Download, ArrowUp, ArrowDown } from 'lucide-react';
import { CSVLink } from 'react-csv';
import { neutralizeCsvRows } from '../utils/csvExport'; // s9csv-4e1b7c28

const toDateInput = (date) => date.toISOString().split('T')[0];

const OverdueEventsReport = ({ currentUserProfile, employees, openJobEditorModal }) => {
    const [overdueEvents, setOverdueEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterEmployeeId, setFilterEmployeeId] = useState('All');
    const [sortConfig, setSortConfig] = useState({ key: 'startAt', direction: 'ascending' });

    const [dateRangeOption, setDateRangeOption] = useState('All Time');
    const [customStartDate, setCustomStartDate] = useState(toDateInput(subMonths(new Date(), 3)));
    const [customEndDate, setCustomEndDate] = useState(toDateInput(new Date()));

    const dateRange = useMemo(() => {
        const now = new Date();
        switch (dateRangeOption) {
            case 'All Time': return { start: null, end: null };
            case 'Today': return { start: startOfDay(now), end: endOfDay(now) };
            case 'Yesterday':
                const yesterday = subDays(now, 1);
                return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
            case 'This Week': return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
            case 'Last Week':
                const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
                return { start: lastWeekStart, end: endOfWeek(lastWeekStart, { weekStartsOn: 1 }) };
            case 'This Month': return { start: startOfMonth(now), end: endOfMonth(now) };
            case 'Last Month':
                const lastMonthStart = startOfMonth(subMonths(now, 1));
                return { start: lastMonthStart, end: endOfMonth(lastMonthStart) };
            case 'Last 3 Months': return { start: startOfDay(subMonths(now, 3)), end: endOfDay(now) };
            case 'Last 6 Months': return { start: startOfDay(subMonths(now, 6)), end: endOfDay(now) };
            case 'Last 12 Months': return { start: startOfDay(subMonths(now, 12)), end: endOfDay(now) };
            case 'YTD': return { start: startOfYear(now), end: endOfDay(now) };
            case 'Custom':
                const start = new Date(`${customStartDate}T00:00:00`);
                const end = new Date(`${customEndDate}T00:00:00`);
                return { start: startOfDay(start), end: endOfDay(end) };
            default: return { start: null, end: null };
        }
    }, [dateRangeOption, customStartDate, customEndDate]);

    useEffect(() => {
        if (!currentUserProfile?.organizationId || (currentUserProfile.role !== 'Admin' && currentUserProfile.role !== 'Manager')) {
            setLoading(false);
            return;
        }
        setLoading(true);
        const orgId = currentUserProfile.organizationId;
        
        let eventsQuery;
        const baseQuery = collection(db, `organizations/${orgId}/jobs`);
        const now = new Date();

        // Base filters for overdue events
        const baseFilters = [
            where('startAt', '<', now),
            where('status', 'in', ['Scheduled', 'In Progress'])
        ];

        if (dateRange.start && dateRange.end) {
            // Apply date range filters in addition to base filters
            eventsQuery = query(baseQuery, ...baseFilters, where('startAt', '>=', dateRange.start), where('startAt', '<=', dateRange.end));
        } else {
            // "All Time" query, only use base filters
            eventsQuery = query(baseQuery, ...baseFilters);
        }

        const unsubscribe = onSnapshot(eventsQuery, (snapshot) => {
            const eventsData = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })); // s6costing-docid: doc id wins over a stale stored `id` field
            setOverdueEvents(eventsData);
            setLoading(false);
        }, (error) => {
            console.error("Firestore Query Error:", error);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [currentUserProfile, dateRange]);


    const getEmployeeNames = (assigneeIds) => {
        if (!assigneeIds || !employees) return 'Unassigned';
        return assigneeIds.map(id => employees.find(e => e.id === id)?.name || 'Unknown').join(', ');
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return <span className="inline-block w-4"></span>;
        return sortConfig.direction === 'ascending' 
            ? <ArrowUp className="inline-block ml-1 h-4 w-4" /> 
            : <ArrowDown className="inline-block ml-1 h-4 w-4" />;
    };

    const sortedAndFilteredEvents = useMemo(() => {
        let filtered = [...overdueEvents];
        if (filterEmployeeId !== 'All') {
            filtered = filtered.filter(event => event.assignedTo?.includes(filterEmployeeId));
        }

        filtered.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (sortConfig.key === 'startAt') {
                aValue = aValue?.toDate();
                bValue = bValue?.toDate();
            } else if (sortConfig.key === 'clientName') {
                aValue = a.clientName || '';
                bValue = b.clientName || '';
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });
        
        return filtered;
    }, [overdueEvents, filterEmployeeId, sortConfig]);

    const csvHeaders = [
        { label: "Event Title", key: "title" },
        { label: "Customer", key: "customer" },
        { label: "Event Type", key: "eventType" },
        { label: "Assigned To", key: "assignedTo" },
        { label: "Due Date", key: "dueDate" },
        { label: "Status", key: "status" },
        { label: "Overdue By", key: "overdueBy" }
    ];

    const csvData = useMemo(() => {
        return sortedAndFilteredEvents.map(event => ({
            title: event.title,
            customer: event.clientName,
            eventType: event.eventType || 'Job',
            assignedTo: getEmployeeNames(event.assignedTo),
            dueDate: event.startAt?.toDate().toLocaleDateString(),
            status: event.status,
            overdueBy: formatDistanceToNow(event.startAt.toDate(), { addSuffix: true })
        }));
    }, [sortedAndFilteredEvents, getEmployeeNames]);

    if (currentUserProfile?.role !== 'Admin' && currentUserProfile?.role !== 'Manager') {
        return <Navigate to="/home" replace />;
    }

    return (
        <div className="p-6 md:p-10">
            <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 mb-2">Overdue Events Report</h1>
                    <p className="text-gray-500">Events that are past their start date but not yet marked 'Completed'.</p>
                </div>
                <CSVLink
                    data={neutralizeCsvRows(csvData)}
                    headers={csvHeaders}
                    filename={`overdue-events-report-${new Date().toISOString().split('T')[0]}.csv`}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-semibold"
                    style={sortedAndFilteredEvents.length === 0 ? { pointerEvents: 'none', opacity: 0.5 } : {}}
                >
                    <Download size={16} />
                    Download CSV
                </CSVLink>
            </div>

            <div className="flex flex-wrap items-end gap-4 p-4 border rounded-lg bg-gray-50 mb-6">
                <div className="flex-grow min-w-[200px]">
                    <label htmlFor="employee-filter" className="block text-sm font-medium text-gray-700">Filter by Employee</label>
                    <select id="employee-filter" value={filterEmployeeId} onChange={(e) => setFilterEmployeeId(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md">
                        <option value="All">All Employees</option>
                        {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                    </select>
                </div>
                <div className="flex-grow min-w-[200px]">
                    <label htmlFor="date-range-filter" className="block text-sm font-medium text-gray-700">Overdue Since</label>
                    <select id="date-range-filter" value={dateRangeOption} onChange={(e) => setDateRangeOption(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md">
                        <option>All Time</option>
                        <option>Today</option>
                        <option>Yesterday</option>
                        <option>This Week</option>
                        <option>Last Week</option>
                        <option>This Month</option>
                        <option>Last Month</option>
                        <option>Last 3 Months</option>
                        <option>Last 6 Months</option>
                        <option>Last 12 Months</option>
                        <option>YTD</option>
                        <option>Custom</option>
                    </select>
                </div>
                {dateRangeOption === 'Custom' && (
                    <>
                        <div className="flex-grow min-w-[150px]">
                            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700">Start Date</label>
                            <input type="date" id="start-date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className="mt-1 block w-full pl-3 pr-2 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" />
                        </div>
                        <div className="flex-grow min-w-[150px]">
                            <label htmlFor="end-date" className="block text-sm font-medium text-gray-700">End Date</label>
                            <input type="date" id="end-date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className="mt-1 block w-full pl-3 pr-2 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" />
                        </div>
                    </>
                )}
            </div>

            <div className="bg-white shadow-sm rounded-lg overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-500">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('title')}>
                                <div className="flex items-center">Event / Customer {getSortIndicator('title')}</div>
                            </th>
                            <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('eventType')}>
                                <div className="flex items-center">Event Type {getSortIndicator('eventType')}</div>
                            </th> 
                            <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('assignedTo')}>
                                <div className="flex items-center">Assigned To {getSortIndicator('assignedTo')}</div>
                            </th>
                            <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('startAt')}>
                                <div className="flex items-center">Due Date {getSortIndicator('startAt')}</div>
                            </th>
                            <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('status')}>
                                <div className="flex items-center">Status {getSortIndicator('status')}</div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="5" className="text-center py-10">Loading report...</td></tr>
                        ) : sortedAndFilteredEvents.length > 0 ? sortedAndFilteredEvents.map(event => (
                            <tr key={event.id} className="bg-white border-b hover:bg-gray-50 cursor-pointer" onClick={() => openJobEditorModal(event)}>
                                <td className="px-6 py-4">
                                    <p className="font-medium text-gray-900">{event.title}</p>
                                    <p className="text-gray-500">{event.clientName}</p>
                                </td>
                                <td className="px-6 py-4">
                                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                                        {event.eventType || 'Job'}
                                    </span>
                                </td>
                                <td className="px-6 py-4">{getEmployeeNames(event.assignedTo)}</td>
                                <td className="px-6 py-4 text-red-600">
                                    {formatDistanceToNow(event.startAt.toDate(), { addSuffix: true })}
                                </td>
                                <td className="px-6 py-4">{event.status}</td>
                            </tr>
                        )) : (
                            <tr><td colSpan="5" className="text-center py-10 text-gray-500">No overdue events found. Great job!</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default OverdueEventsReport;